import { useEffect, useState } from "react";
import type { FormEvent } from "react";

import { ChartSettings, DisplaySettings, ResetPreferences, SavedWhere, TableSettings } from "@strat/ui";

import { DashboardSettings } from "../components/DashboardSettings";
import { NotificationsCard } from "../components/NotificationsCard";
import { PriceAlertsCard } from "../components/PriceAlertsCard";
import { LAYOUT_NAME } from "../nav";
import { useSession, useUpdateMe } from "../session";
import { Guard } from "../shell/Guard";
import { Alert, Card, Field, PageHead } from "../shell/ui";

const SECTIONS: Array<[string, string]> = [
  ["profile", "Profile"],
  ["layout", "Layout"],
  ["display", "Display"],
  ["charts", "Charts"],
  ["dashboard", "Dashboard"],
  ["tables", "Tables"],
  ["alerts", "Alerts & notifications"],
];

/** The tables that remember their columns, sort and filters, by the id each one saves under. */
const TABLE_LABELS: Record<string, string> = {
  "explore-bots": "Explore → Bots",
  "explore-strategies": "Explore → Strategies",
  "my-bots": "My bots",
  benchmarks: "Benchmarks",
  "bot-trades": "A bot's trades",
};

function SettingsInner() {
  const { user } = useSession();
  // Separate saves: the layout switch's success is not the profile form's.
  const updateMe = useUpdateMe();
  const switchLayout = useUpdateMe();
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [bio, setBio] = useState(user?.bio ?? "");
  const [isPublic, setIsPublic] = useState(user?.isPublic ?? true);
  // Load the saved values once per account — not on every change to the user
  // object, which a layout switch makes, and which wiped a bio being typed.
  const userId = user?.id;
  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setBio(user.bio);
    setIsPublic(user.isPublic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  if (!user) return null;

  // "Saved." belongs to what was saved: editing again takes it away.
  const edited =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      if (updateMe.isSuccess) updateMe.reset();
      set(v);
    };
  const onSave = (e: FormEvent) => {
    e.preventDefault();
    updateMe.mutate({ displayName, bio, isPublic });
  };

  return (
    <div className="stack">
      <PageHead title="Settings" />
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {SECTIONS.map(([id, label]) => (
            <a key={id} href={`#${id}`}>
              {label}
            </a>
          ))}
        </nav>
        <div className="stack">
          <Card title="Profile" id="profile">
            <form className="auth__form" onSubmit={onSave}>
              <Field label="Display name">
                <input
                  value={displayName}
                  onChange={(e) => edited(setDisplayName)(e.target.value)}
                  maxLength={48}
                  required
                />
              </Field>
              <Field label="Bio">
                <textarea
                  value={bio}
                  onChange={(e) => edited(setBio)(e.target.value)}
                  maxLength={500}
                  rows={4}
                />
              </Field>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => edited(setIsPublic)(e.target.checked)}
                />
                Public profile — anyone can see it, including visitors who aren't signed in
              </label>
              {updateMe.error && <Alert tone="error">{updateMe.error.message}</Alert>}
              {updateMe.isSuccess && <Alert tone="ok">Saved.</Alert>}
              <div className="row">
                <button className="btn btn--primary" type="submit" disabled={updateMe.isPending}>
                  {updateMe.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </Card>
          <Card title="Layout" id="layout">
            <p>
              You're on the <strong>{LAYOUT_NAME[user.role]}</strong> layout.
            </p>
            {user.canSwitchLayout ? (
              <div className="segmented" role="group" aria-label="Layout">
                {(["starter", "pro"] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    className={user.role === l ? "is-active" : undefined}
                    onClick={() => switchLayout.mutate({ layout: l })}
                    disabled={switchLayout.isPending}
                  >
                    {LAYOUT_NAME[l]}
                  </button>
                ))}
              </div>
            ) : (
              <p className="muted">
                {user.isDemo
                  ? "Demo accounts keep their layout, because other visitors share them."
                  : "Admins always have every tool."}
              </p>
            )}
          </Card>
          <SavedWhere />
          <DisplaySettings />
          <ChartSettings />
          <DashboardSettings />
          <TableSettings labels={TABLE_LABELS} />
          <section id="alerts" className="stack">
            <h2 className="settings-heading">Alerts &amp; notifications</h2>
            <PriceAlertsCard />
            <NotificationsCard />
          </section>
          <ResetPreferences />
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  return (
    <Guard role="starter">
      <SettingsInner />
    </Guard>
  );
}
